/**
 * Form-submit persistence handler extracted verbatim from
 * `ContactEditModal.tsx`. The orchestrator keeps the (non-memoized)
 * `handleSubmit` shell — so the `[handleSubmit]` dependency of the global
 * Enter-key effect still changes identity every render exactly as before —
 * and delegates the body here, parameterized on a typed deps object carrying
 * the field VALUES captured at call time. Behavior is unchanged (#3237 Pass-2).
 */

import { activitiesToNotes } from './contactMapping';
import { parseFormattedNumber } from '../utils';
import type { Activity, ContactObject } from './types';

export interface ContactSubmitDeps {
  firstName: string;
  lastName: string;
  activities: Activity[];
  isNew: boolean | undefined;
  nextStepDueAt: string;
  nextStepAction: string;
  lastSavedFollowUp: { action: string; dueAt: string } | null;
  profile: { id?: string; name?: string } | null | undefined;
  email: string;
  additionalEmails: string[];
  phone: string;
  stage: string;
  label: string;
  labelColor: string;
  company: string;
  lists: string[];
  summary: string;
  dealValue: string;
  probability: string;
  contact: ContactObject | null | undefined;
  xHandle: string;
  linkedinUrl: string;
  contactTitle: string;
  firm: string;
  location: string;
  enrichmentSummary: string;
  enrichmentSkills: string[];
  enrichmentConfidence: string;
  enrichmentAttemptedAt: string;
  estimatedAum: string;
  accreditedStatus: string;
  contactSource: string;
  enrichmentTags: string[];
  enrichHints: string;
  setActivities: (a: Activity[]) => void;
  setLastSavedFollowUp: (f: { action: string; dueAt: string } | null) => void;
  setEnrichmentSummary: (s: string) => void;
  setEnrichmentSkills: (s: string[]) => void;
  setEnrichmentConfidence: (s: string) => void;
  setContactTitle: (s: string) => void;
  setLocation: (s: string) => void;
  setLinkedinUrl: (s: string) => void;
  setSummary: (s: string) => void;
  setFirm: (s: string) => void;
  onSave: (data: Partial<ContactObject>) => void;
}

export function handleSubmit(e: React.FormEvent, deps: ContactSubmitDeps): void {
  const {
    firstName, lastName, activities, isNew, nextStepDueAt, nextStepAction, lastSavedFollowUp,
    profile, email, additionalEmails, phone, stage, label, labelColor, company, lists, summary, dealValue,
    probability, contact, xHandle, linkedinUrl, contactTitle, firm, location, enrichmentSummary,
    enrichmentSkills, enrichmentConfidence, enrichmentAttemptedAt, estimatedAum, accreditedStatus,
    contactSource, enrichmentTags, enrichHints,
    setActivities, setLastSavedFollowUp, setEnrichmentSummary, setEnrichmentSkills,
    setEnrichmentConfidence, setContactTitle, setLocation, setLinkedinUrl, setSummary, setFirm,
    onSave,
  } = deps;

  e.preventDefault();

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();

  if (!fullName) return;

  // Start with current activities
  let finalActivities = activities;

  // Check if follow-up has changed and create a note if needed (only for existing contacts)
  if (!isNew && nextStepDueAt) {
    const hasChanged = !lastSavedFollowUp ||
      lastSavedFollowUp.action !== nextStepAction.trim() ||
      lastSavedFollowUp.dueAt !== nextStepDueAt;

    if (hasChanged) {
      // Format the date for display
      const dueDate = new Date(nextStepDueAt);
      const formattedDate = dueDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      // Create follow-up note
      let followUpNote = nextStepAction.trim()
        ? `Follow Up: ${nextStepAction.trim()} ${formattedDate}`
        : `Follow Up: ${formattedDate}`;

      // Add last touch date if there are previous activities
      if (activities.length > 0) {
        const lastTouchDate = new Date(activities[0].date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
        followUpNote += `\nLast Touch: ${lastTouchDate}`;
      }

      const newActivity: Activity = {
        id: Date.now().toString(),
        date: new Date().toISOString(),
        note: followUpNote,
        authorId: profile?.id || '',
        authorName: profile?.name || 'Unknown',
      };

      finalActivities = [newActivity, ...activities];
      setActivities(finalActivities);

      // Update the tracker
      setLastSavedFollowUp({
        action: nextStepAction.trim(),
        dueAt: nextStepDueAt,
      });
    }
  }

  const data: Partial<ContactObject> = {
    name: fullName,
    // Primary first, then the additional emails — deduped, blanks dropped.
    emails: [...new Set([email, ...additionalEmails].map((e) => e.trim()).filter(Boolean))],
    phones: phone.trim() ? [phone.trim()] : [],
    activities: finalActivities,
    notes: activitiesToNotes(finalActivities),
    stage,
  };

  // Add label if set
  if (label.trim()) {
    data.label = label.trim();
    data.labelColor = labelColor;
  } else {
    data.label = undefined;
    data.labelColor = undefined;
  }

  if (company.trim()) data.organization = company.trim();
  data.lists = lists;
  if (summary.trim()) (data as any).summary = summary.trim();
  if (dealValue.trim()) data.dealValue = parseFormattedNumber(dealValue) || undefined;
  if (probability.trim()) data.probability = parseInt(probability) || undefined;

  // Include next step if date is set (action can be empty)
  if (nextStepDueAt) {
    data.nextStep = {
      action: nextStepAction.trim(), // Can be empty string
      dueAt: new Date(nextStepDueAt).toISOString(),
    };
  } else {
    data.nextStep = undefined;
  }

  // Check if name or company changed — invalidate enrichment if so
  const originalName = contact?.name || contact?.title || '';
  const originalOrg = contact?.organization || contact?.company || (contact?.attributes as any)?.organization || '';
  const nameChanged = !isNew && fullName !== originalName;
  const orgChanged = !isNew && company.trim() !== originalOrg;
  if (nameChanged || orgChanged) {
    // Clear enrichment data — it's stale
    setEnrichmentSummary('');
    setEnrichmentSkills([]);
    setEnrichmentConfidence('');
    setContactTitle('');
    setLocation('');
    setLinkedinUrl('');
    setSummary('');
    setFirm(company.trim());
  }

  // Store extended attributes
  const attributes: any = {};
  if (xHandle.trim()) attributes.xHandle = xHandle.trim();
  if (nameChanged || orgChanged) {
    // Explicitly clear enrichment fields in attributes
    attributes.linkedinUrl = '';
    attributes.jobTitle = '';
    attributes.organization = company.trim();
    attributes.location = '';
    attributes.enrichmentSummary = '';
    attributes.enrichmentSkills = [];
    attributes.enrichmentConfidence = '';
    attributes.enrichmentAttemptedAt = '';
  } else {
    if (linkedinUrl.trim()) attributes.linkedinUrl = linkedinUrl.trim();
    if (contactTitle.trim()) attributes.jobTitle = contactTitle.trim();
    if (firm.trim()) attributes.organization = firm.trim();
    if (location.trim()) attributes.location = location.trim();
    if (enrichmentSummary) attributes.enrichmentSummary = enrichmentSummary;
    if (enrichmentSkills.length) attributes.enrichmentSkills = enrichmentSkills;
    if (enrichmentConfidence) attributes.enrichmentConfidence = enrichmentConfidence;
    if (enrichmentAttemptedAt) attributes.enrichmentAttemptedAt = enrichmentAttemptedAt;
  }
  if (estimatedAum.trim()) attributes.estimatedAum = estimatedAum.trim();
  if (accreditedStatus !== 'unknown') attributes.accreditedInvestorStatus = accreditedStatus;
  if (contactSource.trim()) attributes.source = contactSource.trim();
  if (enrichmentTags.length > 0) attributes.enrichmentTags = enrichmentTags;
  if (Object.keys(attributes).length > 0) {
    (data as any).attributes = attributes;
  }

  // Include AI hints so they persist and can be used for enrichment
  if (enrichHints.trim()) {
    (data as any).enrichHints = enrichHints.trim();
  }

  console.log('[ContactEditModal] Submit:', data);
  onSave(data);
}
